import type { ModelCatalogModel, ModelCatalogProvider } from "@/types/modelCatalog";
import { AUTO_ASSISTANT_MODEL } from "@/types/ai";

export type ModelProvider = ModelCatalogProvider | "unknown";

export const MODEL_CATALOG_REVALIDATE_MS = 5 * 60 * 1000;

export type ModelCatalogSnapshot = {
   models: ModelCatalogModel[];
   isLoading: boolean;
   error: string | null;
   fetchedAt: number | null;
};

let catalogCache: ModelCatalogModel[] | null = null;
let catalogByAlias = new Map<string, ModelCatalogModel>();
let snapshot: ModelCatalogSnapshot = {
   models: [],
   isLoading: true,
   error: null,
   fetchedAt: null,
};
let loadGeneration = 0;
const listeners = new Set<() => void>();

function emit(): void {
   for (const listener of listeners) {
      listener();
   }
}

export function subscribeModelCatalog(listener: () => void): () => void {
   listeners.add(listener);
   return () => {
      listeners.delete(listener);
   };
}

export function getModelCatalogSnapshot(): ModelCatalogSnapshot {
   return snapshot;
}

export function beginModelCatalogLoad(): number {
   const generation = ++loadGeneration;
   snapshot = {
      ...snapshot,
      isLoading: snapshot.fetchedAt == null,
      error: null,
   };
   emit();
   return generation;
}

export function isCurrentModelCatalogLoad(generation: number): boolean {
   return generation === loadGeneration;
}

export function failModelCatalogLoad(generation: number, error: string): void {
   if (generation !== loadGeneration) {
      return;
   }
   snapshot = { ...snapshot, isLoading: false, error };
   emit();
}

export function setModelCatalog(models: ModelCatalogModel[]): void {
   catalogCache = [...models];
   catalogByAlias = new Map(catalogCache.map((model) => [model.alias, model]));
   snapshot = {
      models: catalogCache,
      isLoading: false,
      error: null,
      fetchedAt: Date.now(),
   };
   emit();
}

export function getModelCatalogCache(): ModelCatalogModel[] | null {
   return catalogCache;
}

/** Test isolation only. */
export function resetModelCatalogCache(): void {
   loadGeneration += 1;
   catalogCache = null;
   catalogByAlias = new Map();
   snapshot = {
      models: [],
      isLoading: true,
      error: null,
      fetchedAt: null,
   };
   emit();
}

export function getModelByAlias(alias: string | null | undefined): ModelCatalogModel | undefined {
   if (!alias) {
      return undefined;
   }
   return catalogByAlias.get(alias);
}

export function getModelDisplayName(alias: string): string {
   if (isAutoAssistantModel(alias)) {
      return getModelByAlias(alias)?.displayName ?? "Auto";
   }
   return getModelByAlias(alias)?.displayName ?? alias;
}

export function getModelProvider(alias: string | null | undefined): ModelProvider {
   if (!alias) {
      return "unknown";
   }

   if (isAutoAssistantModel(alias)) {
      return "unknown";
   }

   const catalogProvider = getModelByAlias(alias)?.provider;
   if (catalogProvider) {
      return catalogProvider;
   }

   if (alias.startsWith("claude-")) {
      return "anthropic";
   }
   if (alias.startsWith("gpt-") || alias.startsWith("o4-")) {
      return "openai";
   }
   if (alias.startsWith("gemini-")) {
      return "google";
   }
   return "unknown";
}

export function isDeprecatedModel(alias: string | null | undefined): boolean {
   return getModelByAlias(alias)?.isDeprecated === true;
}

export function isAutoAssistantModel(alias: string | null | undefined): boolean {
   return alias?.trim().toLowerCase() === AUTO_ASSISTANT_MODEL;
}

/** Maps `auto` to the catalog auto-target alias so capability helpers see the real model. */
export function resolveEffectiveModelAlias(
   alias: string | null | undefined
): string | undefined {
   if (!alias) {
      return undefined;
   }
   if (!isAutoAssistantModel(alias)) {
      return alias;
   }
   return catalogCache?.find((model) => model.isAutoTarget)?.alias ?? alias;
}
